import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CaptureFrames } from '@/components/capture/CaptureFrames';
import { AttributeTabs } from '@/components/review/AttributeTabs';
import { QualityCardPanel } from '@/components/review/QualityCardPanel';
import { RefinePanel } from '@/components/review/RefinePanel';
import { OperatorFooter, OutcomePanel, ReviewerFooter, type FooterOutcome } from '@/components/review/ReviewFooter';
import { VoiceNotesPanel } from '@/components/review/VoiceNotesPanel';
import {
  CONFIRMABLE_STATUSES,
  ESCALATABLE_STATUSES,
  FINISHED_STATUSES,
  FRAME_CHANGE_STATUSES,
  MAX_FRAMES,
  RERUNNABLE_STATUSES,
  buildAttributes,
  buildConfirmRequest,
  hasPendingChanges,
  showsReviewerFooter,
  statusLabel,
  isWorking,
  productHeadline,
} from '@/components/review/review-model';
import { useCaptureRecord } from '@/components/review/useCaptureRecord';
import {
  changeFrame,
  confirmCapture,
  escalateCapture,
  rerunCapture,
  submitReviewVerdict,
  uploadCaptureAsset,
} from '@/lib/api';
import type { CorrectionReason, FrameChange, RerunRequest } from '@/lib/api/types';
import { useAuth } from '@/lib/auth/auth-context';
import { useMyAccess } from '@/lib/access/access-context';
import { workflowTitle } from '@/lib/capture-workflows';
import { sha256Fingerprint } from '@/lib/fingerprint';
import { downscaleForUpload } from '@/lib/downscale-image';

/**
 * CA-05, the shared review surface (ADR-006, INTERFACES.md 15.2). The
 * operator lands here seconds after the shutter, and the manager lands here
 * from the console's "Review and fix". Both see the same record laid out the
 * way the Base44 review lays it out, top to bottom: the product headline,
 * the graded quality card, the source frames with a Retake on each and a
 * "+" to add one, the Identity and Descriptions tabs with every attribute of
 * the item standard, the voice notes, the refine panel, and a footer chosen
 * by role.
 *
 * Every attribute renders, read or empty. An empty one is an input, and the
 * required ones still empty sit at the top, because an operator standing at
 * the item can fill a missing model number in seconds, and a reviewer
 * working from a photograph hours later cannot.
 *
 * Controls stay available after a confirm. The operator can still fix a
 * value, retake a frame or ask the manager a question while the capture
 * waits for review. Once it is approved, rejected or staged the review
 * routes answer 409, so the actions disable and the screen says why.
 *
 * The screen polls while the pipeline works on the capture (the first read,
 * or a re-read after a retake or a rerun) and while a voice note is out for
 * transcription. QUARANTINED stops the poll: the capture will not change on
 * its own, so the screen offers a retake instead.
 */

function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

export function ExtractionResult() {
  const { captureId } = useParams<{ captureId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { access } = useMyAccess();
  const { record, error: loadError, stalled, reload, watch } = useCaptureRecord(captureId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, CorrectionReason>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<FooterOutcome | null>(null);
  const [framesChanged, setFramesChanged] = useState(false);

  const reviewer = showsReviewerFooter(access.permissions.reviewCaptures, user?.sub, record?.capturedBy);
  const attributes = useMemo(
    () => (record ? buildAttributes(record, record.attention) : []),
    [record],
  );

  if (loadError && !record) {
    return <p className="text-sm text-warning">{loadError}</p>;
  }
  if (!record || !captureId) {
    return <p className="text-sm text-muted">Loading capture...</p>;
  }

  const assetKeys = record.assetKeys ?? [];
  const working = isWorking(record);
  const quarantined = record.status === 'QUARANTINED';
  const finished = FINISHED_STATUSES.includes(record.status);
  const request = buildConfirmRequest(attributes, drafts, reasons);
  const pendingChanges =
    Object.keys(request.fieldEdits ?? {}).length + Object.keys(request.fieldAdds ?? {}).length;
  const headline = productHeadline(record);
  const retakenBefore = (record.iterations ?? []).some(
    (iteration) => iteration.trigger === 'RETAKE' || iteration.trigger === 'ADD_FRAME',
  );

  function setDraft(name: string, value: string | undefined) {
    setDrafts((current) => {
      const next = { ...current };
      if (value === undefined) delete next[name];
      else next[name] = value;
      return next;
    });
  }

  function setReason(name: string, reason: CorrectionReason | undefined) {
    setReasons((current) => {
      const next = { ...current };
      if (reason === undefined) delete next[name];
      else next[name] = reason;
      return next;
    });
  }

  function clearDrafts() {
    setDrafts({});
    setReasons({});
  }

  async function run<T>(action: () => Promise<T>, fallback: string): Promise<T | null> {
    setBusy(true);
    setActionError(null);
    try {
      return await action();
    } catch (cause: unknown) {
      setActionError(messageOf(cause, fallback));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function sendFrame(file: File, frame: Pick<FrameChange, 'action' | 'index'>) {
    if (!captureId) return;
    const captureJobId = record?.captureJobId;
    const accepted = await run(async () => {
      if (!captureJobId) {
        throw new Error('This capture belongs to no capture job, so a photograph cannot be added.');
      }
      const photo = await downscaleForUpload(file);
      const fingerprint = await sha256Fingerprint(photo);
      const { assetKey } = await uploadCaptureAsset({
        mode: 'photo',
        fingerprint,
        bytes: photo,
        contentType: photo.type || 'image/jpeg',
        captureJobId,
      });
      return changeFrame(captureId, { ...frame, assetKey, fingerprint });
    }, 'Could not send the photograph');
    if (accepted) {
      setFramesChanged(true);
      setOutcome(null);
      watch(accepted.status);
    }
  }

  async function rerun(body: RerunRequest): Promise<boolean> {
    if (!captureId) return false;
    const accepted = await run(() => rerunCapture(captureId, body), 'Could not start Rerun AI');
    if (!accepted) return false;
    setOutcome(null);
    watch(accepted.status);
    return true;
  }

  async function confirm() {
    if (!captureId) return;
    const response = await run(() => confirmCapture(captureId, request), 'Could not save your confirmation');
    if (!response) return;
    clearDrafts();
    setOutcome({ kind: 'confirmed', response });
    await reload();
  }

  async function escalate(question: string): Promise<boolean> {
    if (!captureId) return false;
    const response = await run(() => escalateCapture(captureId, question), 'Could not send the question');
    if (!response) return false;
    setOutcome({ kind: 'escalated' });
    await reload();
    return true;
  }

  /*
    A reviewer's corrections go in before the verdict. Values the extraction
    read go through the verdict route's own "edit", which is the reviewer
    learning signal. Values nobody read cannot be edited there, because the
    route refuses a field the record does not hold, so they go through the
    confirm route, which accepts additions from a reviewer.
  */
  async function verdict(kind: 'approve' | 'reject' | 'request_recapture', reason?: string): Promise<boolean> {
    if (!captureId) return false;
    const response = await run(async () => {
      if (kind === 'approve' && request.fieldAdds) {
        await confirmCapture(captureId, {
          fieldAdds: request.fieldAdds,
          ...(request.reasons ? { reasons: request.reasons } : {}),
        });
      }
      if (kind === 'approve' && request.fieldEdits) {
        await submitReviewVerdict(captureId, { verdict: 'edit', fieldEdits: request.fieldEdits });
      }
      return submitReviewVerdict(captureId, { verdict: kind, ...(reason ? { reason } : {}) });
    }, 'Could not record the verdict');
    if (!response) return false;
    if (kind === 'approve') clearDrafts();
    setOutcome({ kind: 'verdict', verdict: kind, status: response.status });
    await reload();
    return true;
  }

  const canChangeFrames = FRAME_CHANGE_STATUSES.includes(record.status) && !busy;

  return (
    <div className="flex flex-col gap-4 pb-4">
      <header className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-wide text-muted">
          {record.workflow ? workflowTitle(record.workflow) : 'Product'}
        </p>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h1 className="min-w-0 break-words text-lg font-semibold">{headline.title}</h1>
          <div className="flex flex-wrap gap-1">
            {reviewer && <Badge>Reviewing</Badge>}
            <Badge tone={quarantined ? 'danger' : finished ? 'success' : 'neutral'}>{statusLabel(record.status)}</Badge>
          </div>
        </div>
        {!headline.fromNaming && (
          <p className="text-xs text-muted">No noun and modifier were read, so the description stands in.</p>
        )}
      </header>

      {working && (
        <div className="flex items-center gap-2 rounded-card border border-border p-3 text-sm" role="status">
          <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
          <span>
            {stalled
              ? `Still ${statusLabel(record.status).toLowerCase()} after two minutes. It is safe to carry on capturing. A reviewer picks it up either way.`
              : `Reading ${assetKeys.length > 1 ? `all ${assetKeys.length} frames` : 'the photograph'}. The values update here when it finishes.`}
          </span>
        </div>
      )}

      {quarantined && (
        <div className="flex flex-col gap-1 rounded-card border border-danger p-3 text-sm" role="alert">
          <p className="text-danger">This capture could not be read.</p>
          <p className="text-muted">
            The reading stopped after repeated failures. Retake a frame below and it is read again from the start.
          </p>
        </div>
      )}

      {!quarantined && <QualityCardPanel card={record.qualityCard} confidence={record.confidence} />}

      <section aria-label="Source frames" className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">Source frames</h2>
        <CaptureFrames
          captureId={captureId}
          assetKeys={assetKeys}
          maxFrames={MAX_FRAMES}
          busy={!canChangeFrames}
          preferServer={framesChanged || retakenBefore}
          onRetake={(index, file) => void sendFrame(file, { action: 'REPLACE', index })}
          onAddFrame={(file) => void sendFrame(file, { action: 'ADD' })}
        />
      </section>

      {!quarantined && (
        <AttributeTabs
          attributes={attributes}
          assetKeys={assetKeys}
          drafts={drafts}
          reasons={reasons}
          disabled={busy || working}
          onDraft={setDraft}
          onReason={setReason}
        />
      )}

      <VoiceNotesPanel record={record} onRecorded={() => watch()} />

      {!quarantined && (
        <RefinePanel
          captureId={captureId}
          attributes={attributes}
          iterations={record.iterations ?? []}
          canRerun={RERUNNABLE_STATUSES.includes(record.status)}
          busy={busy || working}
          onRerun={rerun}
          onVoiceRecorded={() => watch()}
        />
      )}

      {actionError && (
        <p className="text-sm text-warning" role="alert">
          {actionError}
        </p>
      )}

      {outcome && <OutcomePanel outcome={outcome} />}

      <footer className="flex flex-col gap-2 border-t border-border pt-4">
        {reviewer ? (
          <ReviewerFooter
            busy={busy}
            pendingChanges={pendingChanges}
            canDecide={!working && !finished}
            onVerdict={verdict}
          />
        ) : (
          !quarantined && (
            <OperatorFooter
              pendingChanges={hasPendingChanges(request) ? pendingChanges : 0}
              busy={busy}
              canConfirm={CONFIRMABLE_STATUSES.includes(record.status)}
              canEscalate={ESCALATABLE_STATUSES.includes(record.status)}
              onConfirm={() => void confirm()}
              onEscalate={escalate}
            />
          )
        )}
        {finished && (
          <p className="text-xs text-muted">
            This capture is {record.status.toLowerCase().replace('_', ' ')}, so its values can no longer change here.
          </p>
        )}
        {/* Back to the job the capture belongs to, which is where the
            operator came from. A capture taken before jobs has none. */}
        <Button
          variant="ghost"
          className="w-fit"
          onClick={() => navigate(record.captureJobId ? `/jobs/${encodeURIComponent(record.captureJobId)}` : '/work')}
        >
          {record.captureJobId ? 'Back to the job' : 'Back to my jobs'}
        </Button>
      </footer>
    </div>
  );
}
